/**
 * ==========================================================================
 *  RUN VERIFICATION
 * ==========================================================================
 *  Takes a replay and produces the run it *actually* describes: the time, the
 *  splits, the Hannu hits, whether the player ever sat down at all.
 *
 *  This is the piece that makes the leaderboard mean something. The client
 *  never gets to assert a time; it hands over the inputs, and this function -
 *  running on the server, over the same simulation code the game runs - works
 *  out what those inputs produce. A hand-written POST claiming 00:01.000 loses
 *  here, because the inputs it carries do not reach the chair in one second.
 *
 *  It is also usable from the browser, and the game runs it on its own replay
 *  before submitting: if the local re-simulation disagrees with what was just
 *  played, something is wrong with the recording and we would rather find out
 *  on the client than have the server reject an honest run.
 * ==========================================================================
 */

import { SIMULATION_HZ } from '../config/gameplay';
import { categoryKey, sanitiseLevel, type IntoxicationLevel } from '../config/intoxication';
import { TEST_LEVEL } from '../config/level';
import { RunManager } from '../game/RunManager';
import { RunSimulation } from '../sim/RunSimulation';
import { createSeededLevel } from '../sim/SeededLevel';
import { isSeed } from '../sim/seed';
import { runCategoryKey } from '../score/runCategory';
import { simulationFingerprint } from '../sim/fingerprint';
import type { LevelDefinition } from '../world/Level';
import type { Split } from '../world/Checkpoint';
import type { RunResult, ScoreRepository } from '../score/ScoreRepository';
import { decodeReplay, type Replay } from './ReplayFormat';
import { analyseInputs, type InputAnalysis } from './analysis';

export type VerificationFailure =
  | 'malformed'
  | 'wrong-level'
  | 'wrong-fingerprint'
  | 'wrong-tick-rate'
  | 'not-finished'
  | 'empty';

export interface ReplayVerdict {
  ok: boolean;
  failure: VerificationFailure | null;
  /** Human-readable, in Finnish, because that is what the player will read. */
  message: string;
  /** The authoritative time. Derived here, never taken from the client. */
  timeMs: number;
  splits: Split[];
  hannuHits: number;
  finished: boolean;
  /** Derived from dose, seed and the verifier's own simulation fingerprint. */
  category: string;
  intoxication: IntoxicationLevel;
  frames: number;
  analysis: InputAnalysis;
}

/** The verifier has no interest in personal bests, but RunManager wants one. */
export class NullScoreRepository implements ScoreRepository {
  async loadPersonalBest(): Promise<RunResult | null> {
    return null;
  }
  async savePersonalBest(): Promise<void> {}
  async loadAll(): Promise<RunResult[]> {
    return [];
  }
  async clear(): Promise<void> {}
}

function emptyVerdict(
  failure: VerificationFailure,
  message: string,
  intoxication: IntoxicationLevel,
  frames: number,
): ReplayVerdict {
  return {
    ok: false,
    failure,
    message,
    timeMs: 0,
    splits: [],
    hannuHits: 0,
    finished: false,
    category: categoryKey(intoxication),
    intoxication,
    frames,
    analysis: analyseInputs([], 1),
  };
}

export interface VerifyOptions {
  /** Unseeded base; the replay seed is applied exactly once here. */
  level?: LevelDefinition;
  /** Override for tests. Defaults to this build's own fingerprint. */
  expectedFingerprint?: string;
}

export function verifyReplay(replay: Replay, options: VerifyOptions = {}): ReplayVerdict {
  const base = options.level ?? TEST_LEVEL;
  const intoxication = sanitiseLevel({
    alcoholPromille: replay.header.alcoholPromille,
    drugsPercent: replay.header.drugsPercent,
  });

  if (!isSeed(replay.header.seed) ||
    (replay.header.pinSeed !== undefined && !isSeed(replay.header.pinSeed))) {
    return emptyVerdict('malformed', 'Replayn seed on virheellinen.', intoxication, replay.frames.length);
  }
  const level = createSeededLevel(replay.header.seed, base);
  const expected = options.expectedFingerprint ?? simulationFingerprint(level);
  const fail = (...args: Parameters<typeof emptyVerdict>): ReplayVerdict => ({
    ...emptyVerdict(...args),
    category: runCategoryKey(intoxication, replay.header.seed, expected),
  });

  if (replay.header.levelName !== level.name) {
    return fail(
      'wrong-level',
      `Replay on kentältä "${replay.header.levelName}", palvelin ajaa kenttää "${level.name}".`,
      intoxication,
      replay.frames.length,
    );
  }
  if (replay.header.simulationHz !== SIMULATION_HZ) {
    return fail(
      'wrong-tick-rate',
      `Replay on ${replay.header.simulationHz} Hz, tämä versio ajaa ${SIMULATION_HZ} Hz.`,
      intoxication,
      replay.frames.length,
    );
  }
  if (replay.header.fingerprint !== expected) {
    // Not an accusation: it is usually just an older build of the game.
    return fail(
      'wrong-fingerprint',
      'Replay on eri fysiikkaversiolta - tulosta ei voi validoida tätä buildia vasten.',
      intoxication,
      replay.frames.length,
    );
  }
  if (replay.frames.length === 0) {
    return fail('empty', 'Replay ei sisällä yhtään syötettä.', intoxication, 0);
  }

  const runManager = new RunManager({
    seed: replay.header.seed,
    fingerprint: expected,
    checkpoints: level.checkpoints,
    repository: new NullScoreRepository(),
    playerName: '',
    levelName: level.name,
    intoxication,
    // Wall-clock timestamps are metadata; the verifier must not depend on one.
    now: () => 0,
  });
  const simulation = new RunSimulation(level, runManager, replay.header.pinSeed ?? 0);
  runManager.start();

  let finished = false;
  for (const frame of replay.frames) {
    const events = simulation.step(frame);
    if (events.finished) {
      finished = true;
      break;
    }
  }

  const analysis = analyseInputs(replay.frames, replay.header.mouseSensitivity);

  if (!finished) {
    return {
      ...fail('not-finished', 'Replay ei pääty oikean PIN-koodin syöttämiseen.', intoxication, replay.frames.length),
      timeMs: runManager.elapsedMs,
      splits: [...runManager.checkpoints.reached],
      hannuHits: runManager.hannuHitCount,
      analysis,
    };
  }

  const result = runManager.lastRun?.result ?? null;
  return {
    ok: true,
    failure: null,
    message: 'Replay validoitu.',
    timeMs: result?.timeMs ?? runManager.elapsedMs,
    splits: result ? [...result.splits] : [...runManager.checkpoints.reached],
    hannuHits: result?.hannuHits ?? runManager.hannuHitCount,
    finished: true,
    category: runCategoryKey(intoxication, replay.header.seed, expected),
    intoxication,
    frames: replay.frames.length,
    analysis,
  };
}

/** Convenience for the server, which receives bytes rather than a Replay. */
export function verifyReplayBytes(bytes: Uint8Array, options: VerifyOptions = {}): ReplayVerdict {
  let replay: Replay;
  try {
    replay = decodeReplay(bytes);
  } catch (error) {
    return emptyVerdict(
      'malformed',
      error instanceof Error ? error.message : 'Replay ei aukea.',
      sanitiseLevel({}),
      0,
    );
  }
  return verifyReplay(replay, options);
}
